// src/lib/imageResize.ts

export type ResizeOptions = {
  maxWidth?: number;
  maxHeight?: number;
  mime?: 'image/jpeg' | 'image/webp';
  quality?: number; // 0..1
  rename?: boolean; // suggest new name with extension
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

export async function resizeImageFile(file: File, opts: ResizeOptions = {}): Promise<{ blob: Blob; suggestedName: string; mimeType: string; width: number; height: number } | null> {
  try {
    const url = URL.createObjectURL(file);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);

    const maxW = opts.maxWidth ?? 1280;
    const maxH = opts.maxHeight ?? 1280;
    const { width, height } = img;
    const ratio = Math.min(1, maxW / width, maxH / height);

    // If already within bounds, return null to signal no change
    if (!isFinite(ratio) || ratio >= 1) return null;

    const targetW = Math.max(1, Math.round(width * ratio));
    const targetH = Math.max(1, Math.round(height * ratio));

    const mimeType: string = opts.mime ?? (typeof window !== 'undefined' && (window as any).navigator?.userAgent?.includes('Safari') ? 'image/jpeg' : 'image/webp');
    const quality = typeof opts.quality === 'number' ? opts.quality : 0.82;

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.drawImage(img, 0, 0, targetW, targetH);
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
    if (!blob) throw new Error('Failed to create image blob');

    // Suggest a new filename when changing format
    let suggestedName = file.name;
    if (opts.rename) {
      const dot = file.name.lastIndexOf('.');
      suggestedName = (dot > 0 ? file.name.slice(0, dot) : file.name) + (mimeType === 'image/webp' ? '.webp' : '.jpg');
    }

    return { blob, suggestedName, mimeType, width: targetW, height: targetH };
  } catch {
    return null;
  }
}

export async function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const url = URL.createObjectURL(file);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);
    return { width: img.width, height: img.height };
  } catch {
    return null;
  }
}
